/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-09
 */

// Add third-party dependencies.
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Fade,
  Divider,
} from '@mui/material';
import {
  Save,
  Cancel,
  PhotoCamera,
  Language,
  LinkedIn,
  Instagram,
  Facebook,
} from '@mui/icons-material';

// Add local dependencies.
import { useTranslation } from 'react-i18next';
import AvatarManager from './AvatarManager';
import ProfileFormSkeleton from '../common/ProfileFormSkeleton';
import { useUpdateProfileMutation } from '../../features/auth';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useProfileFormData } from '../../hooks/useProfileData';
import {
  validateBioTranslated,
  validateUrlTranslated,
} from '../../utils/validationTranslated';
import { type ValidationRules } from '../../utils/validation';
import { sanitizeErrorMessage } from '../../utils/security';

/**
 * Form data interface for bio and media editing.
 */
interface BioMediaFormData {
  bio: string;
  website: string;
  linkedin: string;
  instagram: string;
  facebook: string;
}

/**
 * Props for the BioMediaManager component.
 */
interface BioMediaManagerProps {
  /**
   * Callback function called when the form is successfully saved.
   */
  onSave?: () => void;
  /**
   * Callback function called when the user cancels editing.
   */
  onCancel?: () => void;
  /**
   * Whether to show success/error messages inline.
   * @default true
   */
  showMessages?: boolean;
}

/**
 * BioMediaManager component that handles bio, website, social media links, and profile picture.
 * Integrates AvatarManager with bio and social media form fields.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The bio media manager component
 *
 * @example
 * <BioMediaManager
 *   onSave={() => setEditMode(false)}
 *   onCancel={() => setEditMode(false)}
 * />
 */
const BioMediaManager: React.FC<BioMediaManagerProps> = ({
  onSave,
  onCancel,
  showMessages = true,
}) => {
  // Initialize translation hook for validation errors
  const { t } = useTranslation('errors');

  const { initialData, isDataReady, user } = useProfileFormData();
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  const [formData, setFormData] = useState<BioMediaFormData>({
    bio: '',
    website: '',
    linkedin: '',
    instagram: '',
    facebook: '',
  });

  const [apiError, setApiError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Custom validation function for URLs that handles placeholder text.
   * Unlike the standard validateUrl, this allows empty fields and ignores placeholder URLs.
   */
  const validateOptionalUrl = (url: string): string | undefined => {
    // URL is optional, so empty is valid
    if (!url || !url.trim()) {
      return undefined;
    }

    // Ignore placeholder URLs that contain "yourprofile" or similar
    if (url.includes('yourprofile') || url.includes('yourwebsite')) {
      return undefined;
    }

    // Use the translated URL validation for actual URLs
    return validateUrlTranslated(url, t);
  };

  /**
   * Validation schema specifically for the bio form.
   * Uses more lenient validation for optional URL fields.
   */
  const bioFormValidationSchema: ValidationRules = {
    bio: [(value: string) => validateBioTranslated(value, t)],
    website: [validateOptionalUrl],
    linkedin: [validateOptionalUrl],
    instagram: [validateOptionalUrl],
    facebook: [validateOptionalUrl],
  };

  // Initialize validation hook
  const { errors, validateForm, validateField } =
    useFormValidation<BioMediaFormData>(bioFormValidationSchema, {
      validateOnBlur: true,
    });

  // Initialize form data with current user information
  useEffect(() => {
    if (isDataReady && initialData) {
      setFormData({
        bio: initialData.bio || '',
        website: initialData.website || '',
        linkedin: initialData.linkedin || '',
        instagram: initialData.instagram || '',
        facebook: initialData.facebook || '',
      });
    }
  }, [isDataReady, initialData]);

  // Check if form data has changed from original user data
  const hasChanges =
    initialData &&
    (formData.bio !== (initialData.bio || '') ||
      formData.website !== (initialData.website || '') ||
      formData.linkedin !== (initialData.linkedin || '') ||
      formData.instagram !== (initialData.instagram || '') ||
      formData.facebook !== (initialData.facebook || ''));

  /**
   * Handles input changes for form fields.
   * @param e - The input change event
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Use validation hook's input change handler
    const updatedFormData = {
      ...formData,
      [name]: value,
    };
    validateField(name as keyof BioMediaFormData, value, updatedFormData);

    // Clear messages when user starts typing
    if (apiError) setApiError('');
    if (successMessage) setSuccessMessage('');
  };

  /**
   * Handles form submission.
   * @param e - The form submit event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    // Debug logging for form validation
    console.log('[BioMediaManager] Form submission started', {
      formData,
      errors,
      hasChanges,
    });

    const isFormValid = validateForm(formData);
    console.log('[BioMediaManager] Form validation result:', {
      isValid: isFormValid,
      errors,
      formData,
    });

    if (!isFormValid) {
      console.warn(
        '[BioMediaManager] Form validation failed, blocking submission',
        {
          errors,
          formData,
        }
      );
      setIsSubmitting(false);
      return;
    }

    console.log(
      '[BioMediaManager] Form validation passed, proceeding with API call'
    );

    try {
      // Include both user fields and profile fields
      const result = await updateProfile({
        // Keep existing user fields
        email: user?.email || '',
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        phone_number: user?.phone_number || undefined,
        marketing_emails: user?.marketing_emails || false,
        // Add profile fields
        bio: formData.bio,
        website: formData.website,
        linkedin: formData.linkedin,
        instagram: formData.instagram,
        facebook: formData.facebook,
      }).unwrap();

      const message = result.message || 'Bio and media updated successfully!';
      setSuccessMessage(message);

      // Call onSave callback after a brief delay to show success message
      if (onSave) {
        setTimeout(() => {
          onSave();
        }, 1500);
      }
    } catch (error: any) {
      setApiError(sanitizeErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles form cancellation.
   */
  const handleCancel = () => {
    // Reset form to original user data
    if (initialData) {
      setFormData({
        bio: initialData.bio || '',
        website: initialData.website || '',
        linkedin: initialData.linkedin || '',
        instagram: initialData.instagram || '',
        facebook: initialData.facebook || '',
      });
    }
    setApiError('');
    setSuccessMessage('');

    if (onCancel) {
      onCancel();
    }
  };

  /**
   * Handles avatar operation success.
   */
  const handleAvatarSuccess = useCallback(
    (message: string) => {
      if (showMessages) {
        setSuccessMessage(message);
        // Clear success message after a delay
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      }
    },
    [showMessages]
  );

  /**
   * Handles avatar operation error.
   */
  const handleAvatarError = useCallback(
    (error: string) => {
      if (showMessages) {
        setApiError(error);
      }
    },
    [showMessages]
  );

  /**
   * Gets character count for bio field.
   */
  const getBioCharacterCount = () => formData.bio.length;

  /**
   * Gets character count color based on limit.
   */
  const getBioCharacterCountColor = () => {
    const count = getBioCharacterCount();
    if (count > 950) return 'error';
    if (count > 900) return 'warning';
    return 'text.secondary';
  };

  // Show skeleton while data is loading
  if (!isDataReady || !user) {
    return <ProfileFormSkeleton fieldCount={5} />;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Bio & Media
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your profile picture, bio, and social media links
      </Typography>

      {/* Success/Error Messages */}
      {showMessages && (
        <>
          <Fade in={!!apiError}>
            <Box>
              {apiError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {apiError}
                </Alert>
              )}
            </Box>
          </Fade>

          <Fade in={!!successMessage}>
            <Box>
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}
            </Box>
          </Fade>
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
        }}
      >
        {/* Profile Picture Section */}
        <Box sx={{ flex: { xs: '1', md: '0 0 300px' } }}>
          <AvatarManager
            onSuccess={handleAvatarSuccess}
            onError={handleAvatarError}
            showMessages={false} // We handle messages at the parent level
          />
        </Box>

        {/* Bio and Social Media Section */}
        <Box sx={{ flex: 1 }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Bio Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <PhotoCamera fontSize="small" />
                About You
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                error={!!errors.bio}
                helperText={
                  errors.bio || `${getBioCharacterCount()}/1000 characters`
                }
                disabled={isLoading || isSubmitting}
                placeholder="Tell others about yourself, your dance background, teaching style, or anything you'd like to share..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: '2px',
                    },
                    transition: 'all 0.2s ease-in-out',
                  },
                  '& .MuiFormHelperText-root': {
                    color: getBioCharacterCountColor(),
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Website Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Language fontSize="small" />
                Website
              </Typography>
              <TextField
                fullWidth
                label="Website URL"
                name="website"
                type="url"
                value={formData.website}
                onChange={handleInputChange}
                error={!!errors.website}
                helperText={
                  errors.website || 'Your personal or professional website'
                }
                disabled={isLoading || isSubmitting}
                placeholder="https://www.yourwebsite.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: '2px',
                    },
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Social Media Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Social Media
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Connect your social media profiles
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* LinkedIn */}
                <TextField
                  fullWidth
                  label="LinkedIn Profile"
                  name="linkedin"
                  type="url"
                  value={formData.linkedin}
                  onChange={handleInputChange}
                  error={!!errors.linkedin}
                  helperText={errors.linkedin}
                  disabled={isLoading || isSubmitting}
                  placeholder="https://www.linkedin.com/in/yourprofile"
                  InputProps={{
                    startAdornment: (
                      <LinkedIn sx={{ color: '#0077B5', mr: 1 }} />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '2px',
                      },
                      '&.Mui-focused fieldset': {
                        borderWidth: '2px',
                      },
                      transition: 'all 0.2s ease-in-out',
                    },
                  }}
                />

                {/* Instagram */}
                <TextField
                  fullWidth
                  label="Instagram Profile"
                  name="instagram"
                  type="url"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  error={!!errors.instagram}
                  helperText={errors.instagram}
                  disabled={isLoading || isSubmitting}
                  placeholder="https://www.instagram.com/yourprofile"
                  InputProps={{
                    startAdornment: (
                      <Instagram sx={{ color: '#E4405F', mr: 1 }} />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '2px',
                      },
                      '&.Mui-focused fieldset': {
                        borderWidth: '2px',
                      },
                      transition: 'all 0.2s ease-in-out',
                    },
                  }}
                />

                {/* Facebook */}
                <TextField
                  fullWidth
                  label="Facebook Profile"
                  name="facebook"
                  type="url"
                  value={formData.facebook}
                  onChange={handleInputChange}
                  error={!!errors.facebook}
                  helperText={errors.facebook}
                  disabled={isLoading || isSubmitting}
                  placeholder="https://www.facebook.com/yourprofile"
                  InputProps={{
                    startAdornment: (
                      <Facebook sx={{ color: '#1877F2', mr: 1 }} />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '2px',
                      },
                      '&.Mui-focused fieldset': {
                        borderWidth: '2px',
                      },
                      transition: 'all 0.2s ease-in-out',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                mt: 4,
              }}
            >
              <Button
                disabled={isLoading || isSubmitting}
                startIcon={<Cancel />}
                onClick={handleCancel}
                sx={{
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 1,
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || isSubmitting || !hasChanges}
                startIcon={
                  isLoading || isSubmitting ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Save />
                  )
                }
                sx={{
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {isLoading || isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default BioMediaManager;
